// src/components/Fields/ReadOnlyTextFieldComponent.jsx
// ReadOnlyTextFieldComponent (theme-parity with TextFieldComponent)
//
// Goal:
// - Identical rendering path (CustomFieldWrapper + variant/error/helperText)
// - Same controlled-value behavior (internalValue mirrors propValue)
// - NO mutations: no onChange writes, no onBlur writes, no transforms/encode
// - Read-only UX: enabled-but-readonly by default (looks “normal” but not editable)
// - Optional lock/auto cues via helper text + end adornment (without changing theme)
//
// Best-practice behavior (your AUF use case):
// - If disabled=true: visually disabled (use for “no asset yet” or “not applicable”)
// - Else: enabled + readOnly with subtle cues so users don’t think it’s editable

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormControl, InputAdornment, Tooltip } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CustomFieldWrapper from './CustomFieldWrapper';

const ReadOnlyTextFieldComponent = ({
  fieldId,
  fieldMetadata = {},
  value: propValue,
  onFocus,
  disabled = false,
  isSubmitting = false,
  variant,
  error,

  // If caller already provides helper text, we won’t override it.
  helperText,
  externalHelperText,

  /**
   * UX knobs (safe defaults)
   * - showLock: show a lock on the right to signal non-editable
   * - autoHelperText: appended/used when helperText not provided
   * - allowFocus: keep focusability (for copy/select) while still read-only
   */
  showLock = true,
  autoHelperText = 'Auto-filled from the attached asset.',
  allowFocus = true,
}) => {
  // Keep parity with TextFieldComponent: internal state mirrors propValue
  const [internalValue, setInternalValue] = useState(propValue);

  useEffect(() => {
    if (propValue !== internalValue) {
      setInternalValue(propValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue, fieldId]);

  // Always render a string (prevents object render issues)
  const displayValue = useMemo(() => {
    if (internalValue == null) return '';
    if (typeof internalValue === 'string') return internalValue;
    if (typeof internalValue === 'number') return String(internalValue);
    return '';
  }, [internalValue]);

  // If we’re enabled-but-readonly, we want a subtle cue without changing theme.
  // Prefer caller’s helperText. If none, use autoHelperText.
  const resolvedHelperText = useMemo(() => {
    if (helperText != null && String(helperText).length > 0) return helperText;
    // Only show the “auto” hint when the field is not disabled (i.e., it’s populated/meaningful)
    if (!disabled && autoHelperText) return autoHelperText;
    return helperText; // could be undefined
  }, [helperText, disabled, autoHelperText]);

  // Avoid showing lock when the control is actually disabled (disabled already signals “not editable”)
  const shouldShowLock = Boolean(showLock && !disabled);

  // Prevent the “text cursor + feels editable” problem:
  // - keep it enabled so it matches other fields
  // - but remove caret indication and block pointer editing
  // - still allow selection/copy if allowFocus=true
  const readOnlyInputSx = useMemo(() => {
    if (disabled) return {}; // disabled styling handled by MUI

    return {
      cursor: allowFocus ? 'default' : 'not-allowed',
      // Keep selection/copy possible, but don’t look like a typing field
      '& input': {
        cursor: allowFocus ? 'default' : 'not-allowed',
        caretColor: 'transparent',
      },
    };
  }, [disabled, allowFocus]);

  // Lock adornment (end) to clarify “system field”
  const endAdornment = useMemo(() => {
    if (!shouldShowLock) return undefined;

    return (
      <InputAdornment position="end">
        <Tooltip title="System field (read-only)">
          <LockOutlinedIcon sx={{ fontSize: 16, opacity: 0.65 }} />
        </Tooltip>
      </InputAdornment>
    );
  }, [shouldShowLock]);

  return (
    <FormControl fullWidth disabled={disabled || isSubmitting}>
      <CustomFieldWrapper
        fieldId={fieldId}
        label={fieldMetadata.label || 'Field'}
        variant={variant}
        value={displayValue}

        // No-op: read-only field should not push changes
        onChange={() => {}}

        // Keep focus parity so FieldRenderer can still mark isEditing=true if desired
        // (but we also allow caller to turn focus off if they want a stricter lock)
        onFocus={allowFocus ? onFocus : undefined}

        // No-op: do not commit anything on blur
        onBlur={() => {}}

        required={false}
        error={error}
        helperText={resolvedHelperText}
        externalHelperText={externalHelperText}
        placeholder={fieldMetadata.hint || '—'}
        fullWidth
        disabled={disabled || isSubmitting}
        sx={readOnlyInputSx}
        InputProps={{
          readOnly: true,
          endAdornment,
          inputProps: {
            // Parity with TextFieldComponent (even if read-only)
            maxLength: fieldMetadata.maxLength,

            // If you want a stricter “not focusable” lock, caller can set allowFocus=false.
            // TabIndex is applied to the input element by MUI via inputProps.
            ...(allowFocus ? null : { tabIndex: -1 }),
          },
        }}
      />
    </FormControl>
  );
};

ReadOnlyTextFieldComponent.propTypes = {
  fieldId: PropTypes.string.isRequired,
  fieldMetadata: PropTypes.shape({
    label: PropTypes.string,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    externalError: PropTypes.bool,
    externalHelperText: PropTypes.string,
    helperText: PropTypes.string,
    hint: PropTypes.string,
    transformations: PropTypes.object,
  }),
  value: PropTypes.any,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  variant: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  externalHelperText: PropTypes.string,

  // UX knobs
  showLock: PropTypes.bool,
  autoHelperText: PropTypes.string,
  allowFocus: PropTypes.bool,
};

export default ReadOnlyTextFieldComponent;